import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ResumeComparison } from '@/components/resume/ResumeComparison';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Helmet } from 'react-helmet-async';

export default function ResumeOptimizationResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationLevel, setOptimizationLevel] = useState<'light' | 'moderate' | 'aggressive'>('moderate');
  const [result, setResult] = useState<any>(null);

  const { parsedData, fileInfo, mode } = location.state || {};

  useEffect(() => {
    if (!parsedData) {
      navigate('/resume/create');
    }
  }, [parsedData, navigate]);

  const handleOptimize = async () => {
    if (!parsedData) return;

    setOptimizing(true);
    try {
      const { data, error } = await supabase.functions.invoke('optimize-resume', {
        body: {
          resumeContent: parsedData,
          optimizationLevel,
          sourceType: mode || 'upload',
          sourceResumeId: fileInfo?.id,
        }
      });

      if (error) throw error;

      setResult(data);

      toast({
        title: 'Resume optimized!',
        description: `ATS score improved from ${data.atsScoreBefore} to ${data.atsScoreAfter}`,
      });

      // Refresh credits
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error('Optimization error:', error);
      
      if (error.message?.includes('INSUFFICIENT_CREDITS')) {
        toast({
          variant: 'destructive',
          title: 'Insufficient Credits',
          description: 'Please upgrade your plan to continue.',
        });
        setTimeout(() => navigate('/pricing'), 1500);
      } else {
        toast({
          variant: 'destructive',
          title: 'Optimization Failed',
          description: error.message || 'Please try again',
        });
      }
    } finally {
      setOptimizing(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.resumeId) return;

    try {
      toast({
        title: 'Generating PDF',
        description: 'Please wait...',
      });

      const { data, error } = await supabase.functions.invoke('generate-resume-pdf', {
        body: { resumeId: result.resumeId }
      });

      if (error) throw error;

      // Open PDF in new tab
      window.open(data.url, '_blank');

      toast({
        title: 'PDF ready!',
        description: 'Your resume is downloading',
      });
    } catch (error: any) {
      console.error('PDF generation error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to generate PDF',
      });
    }
  };

  const handleSave = async () => {
    if (!result) return;

    try {
      toast({
        title: 'Resume saved',
        description: 'Your optimized resume is in your library',
      });

      // Navigate to resume library
      navigate('/app/resume/library');
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: error.message,
      });
    }
  };

  if (!parsedData) {
    return null;
  }

  if (!result) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Optimize Resume | LoopBaan.AI</title>
        </Helmet>

        <div className="max-w-3xl mx-auto space-y-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div>
            <h1 className="text-3xl font-bold mb-2">Optimize Your Resume</h1>
            <p className="text-muted-foreground">
              Choose how aggressively you want to optimize for ATS compatibility
            </p>
          </div>

          <Card className="p-6 space-y-4">
            <div className="space-y-3">
              <Label htmlFor="optimization-level">Optimization Level</Label>
              <Select 
                value={optimizationLevel} 
                onValueChange={(value: any) => setOptimizationLevel(value)}
                disabled={optimizing}
              >
                <SelectTrigger id="optimization-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">
                    <div className="space-y-1">
                      <div className="font-semibold">Light (1 credit)</div>
                      <div className="text-xs text-muted-foreground">
                        Minor keyword additions, preserve 90% of original
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="moderate">
                    <div className="space-y-1">
                      <div className="font-semibold">Moderate (1 credit)</div>
                      <div className="text-xs text-muted-foreground">
                        Rewrite bullets, add keywords naturally - Recommended
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="aggressive">
                    <div className="space-y-1">
                      <div className="font-semibold">Aggressive (2 credits)</div>
                      <div className="text-xs text-muted-foreground">
                        Full rewrite for maximum ATS match
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-4">
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-2">Preview of Parsed Data:</h3>
                <div className="text-sm space-y-1 text-muted-foreground">
                  <p><strong>Name:</strong> {parsedData.fullName || 'Not found'}</p>
                  <p><strong>Email:</strong> {parsedData.email || 'Not found'}</p>
                  <p><strong>Phone:</strong> {parsedData.phone || 'Not found'}</p>
                  <p><strong>Location:</strong> {parsedData.location || 'Not found'}</p>
                  <p><strong>Experience entries:</strong> {parsedData.experiences?.length || 0}</p>
                  <p><strong>Skills found:</strong> {parsedData.skills?.length || 0}</p>
                </div>
              </div>

              <Button 
                size="lg" 
                className="w-full" 
                onClick={handleOptimize}
                disabled={optimizing}
              >
                {optimizing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Optimizing...
                  </>
                ) : (
                  `Optimize Resume (${optimizationLevel === 'aggressive' ? '2' : '1'} credit)`
                )}
              </Button>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Optimized Resume Results | LoopBaan.AI</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <ResumeComparison
          originalContent={parsedData}
          optimizedContent={result.optimizedContent}
          improvements={result.improvements}
          atsScoreBefore={result.atsScoreBefore}
          atsScoreAfter={result.atsScoreAfter}
          keywordsAdded={result.keywordsAdded}
          onDownload={handleDownload}
          onSave={handleSave}
        />
      </div>
    </DashboardLayout>
  );
}
